/**
 * Utilidades para generar enlaces de calendario
 */

/**
 * Genera un enlace para Google Calendar
 * @param {Object} evento - Objeto con los datos del evento
 * @param {string} evento.titulo - Título del evento
 * @param {string} evento.descripcion - Descripción del evento
 * @param {Date} evento.fechaInicio - Fecha de inicio del evento
 * @param {Date} evento.fechaFin - Fecha de fin del evento (opcional)
 * @param {string} evento.ubicacion - Ubicación del evento (opcional)
 * @returns {string} URL para Google Calendar
 */
export function generarEnlaceGoogleCalendar(evento) {
  const baseUrl = 'https://calendar.google.com/calendar/render'
  
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: evento.titulo || 'Actividad',
    details: evento.descripcion || '',
    location: evento.ubicacion || '',
    dates: formatearFechasParaGoogle(evento.fechaInicio, evento.fechaFin)
  })
  
  return `${baseUrl}?${params.toString()}`
}

/**
 * Genera un enlace para Outlook Calendar
 * @param {Object} evento - Objeto con los datos del evento
 * @param {string} evento.titulo - Título del evento
 * @param {string} evento.descripcion - Descripción del evento
 * @param {Date} evento.fechaInicio - Fecha de inicio del evento
 * @param {Date} evento.fechaFin - Fecha de fin del evento (opcional)
 * @param {string} evento.ubicacion - Ubicación del evento (opcional)
 * @returns {string} URL para Outlook Calendar
 */
export function generarEnlaceOutlookCalendar(evento) {
  const baseUrl = 'https://outlook.live.com/calendar/0/deeplink/compose'
  
  const params = new URLSearchParams({
    subject: evento.titulo || 'Actividad',
    body: evento.descripcion || '',
    location: evento.ubicacion || '',
    startdt: formatearFechaParaOutlook(evento.fechaInicio),
    enddt: formatearFechaParaOutlook(evento.fechaFin || evento.fechaInicio)
  })
  
  return `${baseUrl}?${params.toString()}`
}

/**
 * Genera un enlace para Yahoo Calendar
 * @param {Object} evento - Objeto con los datos del evento
 * @param {string} evento.titulo - Título del evento
 * @param {string} evento.descripcion - Descripción del evento
 * @param {Date} evento.fechaInicio - Fecha de inicio del evento
 * @param {Date} evento.fechaFin - Fecha de fin del evento (opcional)
 * @param {string} evento.ubicacion - Ubicación del evento (opcional)
 * @returns {string} URL para Yahoo Calendar
 */
export function generarEnlaceYahooCalendar(evento) {
  const baseUrl = 'https://calendar.yahoo.com/'
  
  const params = new URLSearchParams({
    v: '60',
    view: 'd',
    type: '20',
    title: evento.titulo || 'Actividad',
    st: formatearFechaParaYahoo(evento.fechaInicio),
    et: formatearFechaParaYahoo(evento.fechaFin || evento.fechaInicio),
    desc: evento.descripcion || '',
    in_loc: evento.ubicacion || ''
  })
  
  return `${baseUrl}?${params.toString()}`
}

/**
 * Genera un archivo ICS para descargar
 * @param {Object} evento - Objeto con los datos del evento
 * @param {string} evento.titulo - Título del evento
 * @param {string} evento.descripcion - Descripción del evento
 * @param {Date} evento.fechaInicio - Fecha de inicio del evento
 * @param {Date} evento.fechaFin - Fecha de fin del evento (opcional)
 * @param {string} evento.ubicacion - Ubicación del evento (opcional)
 * @returns {string} Contenido del archivo ICS
 */
export function generarArchivoICS(evento) {
  const fechaInicio = formatearFechaParaICS(evento.fechaInicio)
  const fechaFin = formatearFechaParaICS(evento.fechaFin || evento.fechaInicio)
  const fechaActual = formatearFechaParaICS(new Date())
  
  return `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Gescom//Actividad//ES
BEGIN:VEVENT
UID:${Date.now()}@gescom.com
DTSTAMP:${fechaActual}
DTSTART:${fechaInicio}
DTEND:${fechaFin}
SUMMARY:${evento.titulo || 'Actividad'}
DESCRIPTION:${evento.descripcion || ''}
LOCATION:${evento.ubicacion || ''}
END:VEVENT
END:VCALENDAR`
}

/**
 * Descarga un archivo ICS
 * @param {Object} evento - Objeto con los datos del evento
 */
export function descargarArchivoICS(evento) {
  const contenido = generarArchivoICS(evento)
  const blob = new Blob([contenido], { type: 'text/calendar;charset=utf-8' })
  const url = window.URL.createObjectURL(blob)
  
  const link = document.createElement('a')
  link.href = url
  link.download = `${evento.titulo || 'actividad'}.ics`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  
  window.URL.revokeObjectURL(url)
}

/**
 * Formatea fechas para Google Calendar
 * @param {Date} fechaInicio - Fecha de inicio
 * @param {Date} fechaFin - Fecha de fin
 * @returns {string} Fechas formateadas
 */
function formatearFechasParaGoogle(fechaInicio, fechaFin) {
  const inicio = formatearFechaParaGoogle(fechaInicio)
  const fin = formatearFechaParaGoogle(fechaFin || fechaInicio)
  return `${inicio}/${fin}`
}

/**
 * Formatea una fecha para Google Calendar
 * @param {Date} fecha - Fecha a formatear
 * @returns {string} Fecha formateada
 */
function formatearFechaParaGoogle(fecha) {
  return fecha.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z'
}

/**
 * Formatea una fecha para Outlook Calendar
 * @param {Date} fecha - Fecha a formatear
 * @returns {string} Fecha formateada
 */
function formatearFechaParaOutlook(fecha) {
  return fecha.toISOString()
}

/**
 * Formatea una fecha para Yahoo Calendar
 * @param {Date} fecha - Fecha a formatear
 * @returns {string} Fecha formateada
 */
function formatearFechaParaYahoo(fecha) {
  return fecha.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z'
}

/**
 * Formatea una fecha para archivos ICS
 * @param {Date} fecha - Fecha a formatear
 * @returns {string} Fecha formateada
 */
function formatearFechaParaICS(fecha) {
  return fecha.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z'
}

/**
 * Crea un objeto de evento a partir de una actividad
 * @param {Object} actividad - Objeto de actividad
 * @param {string} hora - Hora en formato HH:MM (opcional, por defecto 09:00)
 * @returns {Object} Objeto de evento para calendario
 */
export function crearEventoDesdeActividad(actividad, hora = '09:00') {
  // Usar la fecha de próxima acción para el recordatorio
  const fechaBase = actividad.proximaAccionFecha ? new Date(actividad.proximaAccionFecha) : new Date(actividad.fecha)
  
  // Combinar la fecha con la hora seleccionada
  const [horas, minutos] = hora.split(':').map(Number)
  const fechaEvento = new Date(fechaBase)
  fechaEvento.setHours(horas, minutos, 0, 0)
  
  // Crear fecha de fin (1 hora después)
  const fechaFin = new Date(fechaEvento)
  fechaFin.setHours(fechaEvento.getHours() + 1)
  
  return {
    titulo: actividad.tipoActividad?.nombre || 'Actividad',
    descripcion: actividad.observaciones || `Actividad del proyecto: ${actividad.nombreProyecto || 'Sin proyecto'}`,
    fechaInicio: fechaEvento,
    fechaFin: fechaFin,
    ubicacion: actividad.nombreProyecto || ''
  }
}
